import styled from "styled-components";
import { BiUpvote, BiDownvote, BiMessageRounded } from "react-icons/bi";
import { HiOutlineRefresh } from "react-icons/hi";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { RiShareForwardLine } from "react-icons/ri";
import { MyComment } from "./MyComment";
import { GoArrowUp, GoArrowDown } from "react-icons/go";
import { useState, useEffect } from "react";
import db from "../../config/firebase.config";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";

export const IconGrp = ({
    handleComments,
    id,           // question key
    showComments,
    toogle,
    questionId,   // same as id, passed from Answers.js
}) => {
    const user = useSelector(selectUser);
    const [upvotes, setUpvotes] = useState(0);
    const [downvotes, setDownvotes] = useState(0);
    const [reshares, setReshares] = useState(0);
    const [commentCount, setCommentCount] = useState(0);
    const [userUpvoted, setUserUpvoted] = useState(false);
    const [userDownvoted, setUserDownvoted] = useState(false);
    const [userReshared, setUserReshared] = useState(false);
    const [loading, setLoading] = useState(true);

    const qId = id || questionId;

    // Live listener on the question document for votes/reshares
    useEffect(() => {
        if (!qId) return;
        const unsub = db.collection("questions").doc(qId).onSnapshot((doc) => {
            if (doc.exists) {
                const data = doc.data();
                const ups = data.up_votes || 0;
                const downs = data.down_votes || 0;
                const rs = data.reshares || 0;
                const upVoters = data.upvoters || [];
                const downVoters = data.downvoters || [];
                const resharers = data.resharers || [];
                setUpvotes(ups);
                setDownvotes(downs);
                setReshares(rs);
                setUserUpvoted(upVoters.includes(user?.uid));
                setUserDownvoted(downVoters.includes(user?.uid));
                setUserReshared(resharers.includes(user?.uid));
            }
            setLoading(false);
        });
        return () => unsub();
    }, [qId, user?.uid]);

    // Live listener for comment count
    useEffect(() => {
        if (!qId) return;
        const unsub = db
            .collection(`questions/${qId}/comments`)
            .onSnapshot((snap) => setCommentCount(snap.size));
        return () => unsub();
    }, [qId]);

    const handleUpvote = async () => {
        if (!user || !qId) return;
        const ref = db.collection("questions").doc(qId);
        const doc = await ref.get();
        if (!doc.exists) return;
        const data = doc.data();
        const upVoters = data.upvoters || [];
        const downVoters = data.downvoters || [];
        const alreadyUp = upVoters.includes(user.uid);
        const alreadyDown = downVoters.includes(user.uid);

        await ref.update({
            up_votes: alreadyUp ? (data.up_votes || 1) - 1 : (data.up_votes || 0) + 1,
            upvoters: alreadyUp
                ? upVoters.filter((u) => u !== user.uid)
                : [...upVoters, user.uid],
            // remove from downvoters if switching
            down_votes: alreadyDown ? (data.down_votes || 1) - 1 : (data.down_votes || 0),
            downvoters: alreadyDown
                ? downVoters.filter((u) => u !== user.uid)
                : downVoters,
        });
    };

    const handleDownvote = async () => {
        if (!user || !qId) return;
        const ref = db.collection("questions").doc(qId);
        const doc = await ref.get();
        if (!doc.exists) return;
        const data = doc.data();
        const downVoters = data.downvoters || [];
        const upVoters = data.upvoters || [];
        const alreadyDown = downVoters.includes(user.uid);
        const alreadyUp = upVoters.includes(user.uid);

        await ref.update({
            down_votes: alreadyDown ? (data.down_votes || 1) - 1 : (data.down_votes || 0) + 1,
            downvoters: alreadyDown
                ? downVoters.filter((u) => u !== user.uid)
                : [...downVoters, user.uid],
            up_votes: alreadyUp ? (data.up_votes || 1) - 1 : (data.up_votes || 0),
            upvoters: alreadyUp
                ? upVoters.filter((u) => u !== user.uid)
                : upVoters,
        });
    };

    const handleReshare = async () => {
        if (!user || !qId) return;
        const ref = db.collection("questions").doc(qId);
        const doc = await ref.get();
        if (!doc.exists) return;
        const data = doc.data();
        const resharers = data.resharers || [];
        const alreadyShared = resharers.includes(user.uid);

        await ref.update({
            reshares: alreadyShared ? (data.reshares || 1) - 1 : (data.reshares || 0) + 1,
            resharers: alreadyShared
                ? resharers.filter((u) => u !== user.uid)
                : [...resharers, user.uid],
        });
    };

    return (
        <>
            <Icongroup>
                <LeftGroup>
                    {/* Upvote */}
                    <ActionBtn
                        className={`vote-btn ${userUpvoted ? "active-up" : ""}`}
                        onClick={handleUpvote}
                        title="Upvote"
                    >
                        {userUpvoted ? <GoArrowUp className="icon filled" /> : <BiUpvote className="icon" />}
                        <span>{upvotes || 0}</span>
                    </ActionBtn>

                    <Divider />

                    {/* Downvote */}
                    <ActionBtn
                        className={`vote-btn right ${userDownvoted ? "active-down" : ""}`}
                        onClick={handleDownvote}
                        title="Downvote"
                    >
                        {userDownvoted ? <GoArrowDown className="icon filled-down" /> : <BiDownvote className="icon" />}
                    </ActionBtn>

                    {/* Reshare */}
                    <ActionBtn
                        className={`round-btn ${userReshared ? "active-share" : ""}`}
                        onClick={handleReshare}
                        title="Reshare"
                    >
                        <HiOutlineRefresh className="icon" />
                        <span>{reshares || 0}</span>
                    </ActionBtn>

                    {/* Comment */}
                    <ActionBtn
                        className={`round-btn ${id === showComments && toogle ? "active-comment" : ""}`}
                        onClick={() => handleComments && handleComments(id)}
                        title="Comment"
                    >
                        <BiMessageRounded className="icon" />
                        <span>{commentCount || 0}</span>
                    </ActionBtn>
                </LeftGroup>

                <RightGroup>
                    {/* Share externally */}
                    <IconCircle title="Share">
                        <RiShareForwardLine />
                    </IconCircle>
                    {/* More options */}
                    <IconCircle title="More">
                        <BiDotsHorizontalRounded />
                    </IconCircle>
                </RightGroup>
            </Icongroup>

            {id === showComments && toogle && (
                <MyComment questionId={questionId || id} />
            )}
        </>
    );
};

/* ─── Styled Components ─── */

const Icongroup = styled.div`
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    border-top: 1px solid rgba(201, 160, 99, 0.12);
    margin-top: 4px;
`;

const LeftGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const RightGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const ActionBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 5px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #636466;
    font-size: 18px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 20px;
    transition: background 0.15s, color 0.15s;

    .icon {
        font-size: 1.3rem;
    }

    &:hover {
        background: #f0ebe3;
        color: #0a1428;
    }

    /* Upvote pill shape */
    &.vote-btn {
        border: 1px solid #e0dbd1;
        border-radius: 20px 0 0 20px;
        padding: 5px 10px 5px 12px;
    }
    &.vote-btn.right {
        border-radius: 0 20px 20px 0;
        border-left: none;
        padding: 5px 12px 5px 8px;
    }

    /* Active states */
    &.active-up {
        color: #1DA1F2;
        background: #e8f5fe;
        border-color: #1DA1F2;
        .icon { color: #1DA1F2; }
    }
    &.active-down {
        color: #e53935;
        background: #ffebee;
        border-color: #e53935;
        .icon { color: #e53935; }
    }
    &.active-share {
        color: #22c55e;
        background: #f0fdf4;
        .icon { color: #22c55e; }
    }
    &.active-comment {
        color: #c9a063;
        background: #faf7f2;
        .icon { color: #c9a063; }
    }

    &.round-btn {
        border-radius: 20px;
        border: none;
        margin-left: 4px;
    }

    span {
        font-size: 18px;
        font-weight: 700;
    }
`;

const Divider = styled.div`
    width: 1px;
    height: 20px;
    background-color: #e0dbd1;
`;

const IconCircle = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    color: #636466;
    font-size: 1.25rem;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    &:hover {
        background: #f0ebe3;
        color: #0a1428;
    }
`;
